import i18n from "../config/translations/i18n";
import { months, weekDays } from "./calendar-utilities";


export enum DateFormats {
    SHORT = 'dd MMM',
    LONG = 'dd/MM/yyyy',
    ISO = 'yyyy-MM-dd',
    LONG_WEEK_SHORT_DATE = 'DDDD, dd MMM',
    DATE = 'yyyy-MM-dd'
}

export enum DateComparisonResult {
    GREATER = 'gt',
    LOWER = 'lt',
    EQUAL = 'eq'
}

export enum DateComparisons {
    GREATER = 'gt',
    GREATER_OR_EQUAL = 'gte',
    LOWER = 'lt',
    LOWER_OR_EQUAL = 'lte',
    EQUAL = 'eq'
}

export class DateUtil {

    static parse(date: string | undefined, format?: DateFormats) {
        if (typeof date != 'string') return undefined;
        format = format || DateFormats.LONG;
        let theDay = new Date();
        let day: number | null = null;
        let month: number | null = null;
        let year: number | null = null;

        switch (format) {
            case DateFormats.SHORT:
                if (!/[0-9]{2}[ ][a-zA-Z]{3}/g.test(date)) {
                    return undefined;
                }

                const shortParts = date.split(' ');
                day = parseInt(shortParts[0]);
                year = theDay.getFullYear();
                month = months.findIndex(m => i18n.t('calendar.months.' + m + '.short') === shortParts[1]);
                break;
            case DateFormats.LONG:
                if (!/[0-9]{2}[/][0-9]{2}[/][0-9]{4}/g.test(date)) {
                    return undefined;
                }
                const longParts = date.split('/');
                day = parseInt(longParts[0]);
                month = parseInt(longParts[1]) - 1;
                year = parseInt(longParts[2]);
                break;
            case DateFormats.DATE:
            case DateFormats.ISO:
                if (!/[0-9]{4}[-][0-9]{2}[-][0-9]{2}/g.test(date)) {
                    return undefined;
                }
                const isoParts = date.split('-');
                day = parseInt(isoParts[2]);
                month = parseInt(isoParts[1]) - 1;
                year = parseInt(isoParts[0]);
                break;
            case DateFormats.LONG_WEEK_SHORT_DATE:
                if (!/[a-zA-Z]{3}[,][ ][0-9]{2}[ ][a-zA-Z]{3}/g.test(date)) {
                    return undefined;
                }
                
                const longWeekShortParts = date.split(' ');
                day = parseInt(longWeekShortParts[1]);
                month = months.findIndex(m => i18n.t('calendar.months.' + m + '.short') === longWeekShortParts[2]);
                year = theDay.getFullYear();
                break;
            default:
                throw new Error('Unsupported date format');
        }

        if (day && month && year) {
            return new Date(year, month, day);
        }

        return undefined;
    }

    static isValid(date: Date | number | string | undefined, format?: DateFormats) {
        if (!date || date == null) return false;
        if (typeof date == 'string') {
            return this.parse(date, format) ? true : false;
        }

        var anyDate = new Date(date) as any;
        if (anyDate === 'Invalid Date') return false;

        return true;
    }

    static ToDate(date: Date | number | string | undefined, format?: DateFormats) {
        if (!this.isValid(date, format)) {
            return undefined;
        }

        if (typeof date == 'string') {
            date = this.parse(date, format);
        }

        return new Date(date!);
    }

    static format(date: Date | number | string, format?: string) {
        const theDate = new Date(date);
        if (!theDate) return '';
        const dayDate = pad(theDate.getDate(), 2);
        if (format === DateFormats.SHORT) {
            var month = i18n.t('calendar.months.' + months[theDate.getMonth()] + '.short');
            return `${dayDate} ${month}`;
        } else if (format === DateFormats.ISO)
            return this.toIsoDate(theDate);
        else if (format === DateFormats.LONG_WEEK_SHORT_DATE) {
            var month = i18n.t('calendar.months.' + months[theDate.getMonth()] + '.short');
            var dayOfWeek = i18n.t('calendar.weeks.' + weekDays[theDate.getDay()] + '.name');
            return `${dayOfWeek}, ${theDate.getDate()} ${month}`;
        }
        else if (format === DateFormats.DATE) {
            return `${theDate.getFullYear()}-${pad(theDate.getMonth() + 1, 2)}-${pad(theDate.getDate(), 2)}`;
        }
        else return `${dayDate}/${pad(theDate.getMonth() + 1, 2)}/${theDate.getFullYear()}`;
    }

    static toIsoDate(date?: Date) {
        const d = date ? new Date(date)! : new Date()!;
        return `${d.getFullYear()}-${pad(d.getMonth() + 1, 2)}-${pad(d.getDate(), 2)}`;
    }

    static addMissingDates(initDates: (Date | undefined | null)[] | undefined): Date[] {
        if (!initDates || !initDates.length || initDates.length === 1) return [];
        let dateData: Date[] = [];
        let dates = initDates.filter(d => d && d != null).map(d => new Date(d!));
        dates = dates.sort((a, b) => a.getTime() - b.getTime());
        const stringDates = dates.map(d => this.toIsoDate(d));
        let dateString = stringDates[0];
        const lastDate = stringDates[stringDates.length - 1];
        while (dateString <= lastDate) {
            let date = new Date(dateString);
            if (!dateData.length || dates.find(d => !this.compareOnAsIsoDate(date, d))) {
                dateData.push(new Date(date));
            }
            const theDate = new Date(date);
            theDate.setDate(theDate.getDate() + 1);
            dateString = this.toIsoDate(theDate);
        }

        return dateData;
    }

    static compareOnAsIsoDate(date1: Date, date2: Date, condition: DateComparisons | DateComparisons[] = DateComparisons.EQUAL): boolean {
        const dateFirst = this.toIsoDate(new Date(date1)!);
        const dateSecond = this.toIsoDate(new Date(date2)!);
        const conditions = Array.isArray(condition) ? condition : [condition];
        for (let index = 0; index < conditions.length; index++) {
            const conditionElement = conditions[index];
            switch (conditionElement) {
                case "gt":
                    return dateFirst > dateSecond;
                case "gte":
                    return dateFirst >= dateSecond;
                case "lt":
                    return dateFirst < dateSecond;
                case "lte":
                    return dateFirst <= dateSecond;
                case "eq":
                default:
                    return dateFirst === dateSecond;
            }
        }
        return false;
    }

    static isoDateComperable(date1: Date, date2: Date = new Date()): DateComparisonResult {
        const dateFirst = this.toIsoDate(new Date(date1)!);
        const dateSecond = this.toIsoDate(new Date(date2)!);
        if (dateFirst == dateSecond) return DateComparisonResult.EQUAL;
        if (dateFirst < dateSecond) return DateComparisonResult.LOWER;
        return DateComparisonResult.GREATER;
    }

    static beginningOfDay(date?: Date) {
        const theDay = date ?? new Date();
        return new Date(theDay.getFullYear(), theDay.getMonth(), theDay.getDate());
    }
}

function pad(num: number, size: number) {
    let res = num.toString();
    while (res.length < size) res = "0" + res;
    return res;
}
